import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import redditPixel from '../lib/tracking';
import { Target, Shield, Zap, Users, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CodyFullLogo, CodyIconBadge } from '../components/CodyLogo';
import { toast } from 'sonner';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await signup(email, password);
      toast.success('Account created!');
      redditPixel.signUp(email);
      navigate('/verify-email', { replace: true });
    } catch (err) { toast.error(err.message || 'Signup failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left — Value Proposition (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B0F17] relative overflow-hidden flex-col justify-center px-16 xl:px-24">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute w-[400px] h-[400px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,69,0,0.1) 0%, transparent 70%)', top: '-10%', left: '-10%' }} />
          <div className="absolute w-[300px] h-[300px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.06) 0%, transparent 70%)', bottom: '10%', right: '5%' }} />
        </div>

        <div className="relative">
          <div className="mb-10">
            <CodyFullLogo height={32} className="brightness-0 invert" />
          </div>

          <h1 className="text-[2.5rem] xl:text-[3rem] font-extrabold text-white leading-[1.1] mb-5" style={{ fontFamily: "'Space Grotesk'" }}>
            You're missing customers already asking for your product.
          </h1>
          <p className="text-lg text-[#8B95A9] mb-10 leading-relaxed max-w-lg">
            Get started for free
          </p>

          <div className="space-y-4 mb-12">
            {[
              'Real-time Reddit scanning for buyer intent',
              'AI-powered reply suggestions',
              'Intent scoring from 0–100',
              'Slack & email alerts for hot leads',
              'Used by 300+ founders & marketers',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-[#16A34A]/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-[#16A34A]" />
                </div>
                <span className="text-sm text-[#C1C7D0]">{item}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {['#FF4500','#2563EB','#16A34A','#D97706'].map((c,i) => (
                <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0B0F17] flex items-center justify-center text-[10px] font-bold text-white" style={{ background: c }}>
                  {['S','M','J','A'][i]}
                </div>
              ))}
            </div>
            <p className="text-xs text-[#5B667A]">Join 300+ founders finding customers on Reddit</p>
          </div>
        </div>
      </div>

      {/* Right — Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-2 mb-8 lg:hidden">
            <CodyIconBadge size={44} />
            <span className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk'" }}>Cody</span>
          </div>

          <div className="hidden lg:block mb-8">
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Create your account</h2>
            <p className="text-sm text-[#5B667A]">Start discovering Reddit leads for free</p>
          </div>

          {/* Mobile-only headline */}
          <div className="lg:hidden text-center mb-6">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Create your account</h2>
            <p className="text-sm text-[#5B667A]">Start discovering Reddit leads for free</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#0B0F17] mb-1.5 block">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="h-12 rounded-xl" data-testid="signup-email-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0B0F17] mb-1.5 block">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required className="h-12 rounded-xl" data-testid="signup-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl font-bold text-[15px] cta-btn shadow-lg shadow-[#FF4500]/20" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }} data-testid="signup-submit-button">
              {loading ? 'Creating account...' : 'Create Free Account'} {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
            </Button>
          </form>

          <p className="text-[11px] text-[#9ca3af] text-center mt-4">Cancel anytime • No contracts • Takes 10 seconds to start</p>

          <p className="text-sm text-[#5B667A] text-center mt-6">
            Already have an account? <Link to="/login" className="text-[#FF4500] font-medium hover:underline" data-testid="login-link">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
